/* ==========================================================================
   TheraMotion — tm-sections.js
   Behaviour for the product-page story sections (tm-*).
   ========================================================================== */
(() => {
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Ticker / marquee: px-per-second speed → animation duration ---------- */
  const sizeTicker = (track) => {
    const speed = parseFloat(track.dataset.speed) || 40;
    const half = track.scrollWidth / 2;
    if (!half) return;
    track.style.setProperty('--tm-dur', `${(half / speed).toFixed(2)}s`);
    track.classList.add('is-running');
  };
  const initTickers = (root = document) => {
    const tracks = qsa('[data-tm-ticker]', root);
    tracks.forEach((track) => {
      if (reduceMotion) return;
      sizeTicker(track);
    });
    return tracks;
  };

  /* ---------- Reveal on scroll (replays each time the item re-enters) ---------- */
  const revealObserver =
    'IntersectionObserver' in window && !reduceMotion
      ? new IntersectionObserver(
          (entries) => entries.forEach((en) => en.target.classList.toggle('is-in', en.isIntersecting)),
          { threshold: 0.2, rootMargin: '0px 0px -100px 0px' }
        )
      : null;
  const initReveal = (root = document) => {
    qsa('[data-tm-reveal]', root).forEach((el) => {
      if (revealObserver) revealObserver.observe(el);
      else el.classList.add('is-in');
    });
  };

  /* ---------- Review wall (moving rows, modal, show all) ---------- */
  class TMReviewWall extends HTMLElement {
    connectedCallback() {
      this.state = { user: false, hover: false, focus: false, offscreen: true, hidden: document.hidden, modal: false, expanded: false };
      this.mq = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.toggleBtn = this.querySelector('[data-rw-toggle]');
      this.modal = this.querySelector('[data-rw-modal]');
      this.modalContent = this.querySelector('[data-rw-modal-content]');
      this.grid = this.querySelector('[data-rw-grid]');
      this.allBtn = this.querySelector('[data-rw-all]');

      this.toggleBtn && this.toggleBtn.addEventListener('click', () => {
        this.state.user = !this.state.user;
        this.update();
      });
      this.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') { this.state.hover = true; this.update(); } });
      this.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') { this.state.hover = false; this.update(); } });
      this.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'touch' || e.target.closest('[data-rw-toggle]')) return;
        this.state.user = true;
        this.update();
      });
      this.addEventListener('focusin', (e) => { if (e.target.closest('.tm-rw__card')) { this.state.focus = true; this.update(); } });
      this.addEventListener('focusout', () => { this.state.focus = false; this.update(); });
      this.addEventListener('click', (e) => {
        const card = e.target.closest('[data-rw-open]');
        if (card) this.open(card);
      });
      this.allBtn && this.allBtn.addEventListener('click', () => this.toggleAll());
      if (this.modal) {
        this.querySelector('[data-rw-close]').addEventListener('click', () => this.modal.close());
        this.modal.addEventListener('click', (e) => { if (e.target === this.modal) this.modal.close(); });
        this.modal.addEventListener('close', () => {
          this.state.modal = false;
          this.update();
          this.opener && this.opener.focus({ preventScroll: true });
        });
      }
      this.onVisibility = () => { this.state.hidden = document.hidden; this.update(); };
      document.addEventListener('visibilitychange', this.onVisibility);
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(([en]) => { this.state.offscreen = !en.isIntersecting; this.update(); }, { threshold: 0 });
        this.io.observe(this);
      } else {
        this.state.offscreen = false;
      }
      this.mq.addEventListener && this.mq.addEventListener('change', () => this.update());
      this.update();
    }
    disconnectedCallback() {
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.io && this.io.disconnect();
    }
    update() {
      const s = this.state;
      const paused = s.user || s.hover || s.focus || s.offscreen || s.hidden || s.modal || s.expanded || this.mq.matches;
      this.dataset.paused = String(paused);
      if (this.toggleBtn) {
        this.toggleBtn.setAttribute('aria-pressed', String(s.user));
        const label = this.toggleBtn.querySelector('[data-rw-toggle-label]');
        const icon = this.toggleBtn.querySelector('[data-rw-toggle-icon]');
        if (label) label.textContent = s.user ? label.dataset.stop : label.dataset.run;
        if (icon) icon.textContent = s.user ? '▶' : 'Ⅱ';
      }
    }
    open(card) {
      const tpl = this.querySelector(`template[data-rw-template="${card.dataset.rwOpen}"]`);
      if (!tpl || !this.modal) return;
      this.opener = card;
      this.modalContent.innerHTML = '';
      this.modalContent.appendChild(tpl.content.cloneNode(true));
      this.state.modal = true;
      this.update();
      if (typeof this.modal.showModal === 'function') this.modal.showModal();
      else this.modal.setAttribute('open', '');
    }
    toggleAll() {
      this.state.expanded = !this.state.expanded;
      this.classList.toggle('is-expanded', this.state.expanded);
      if (this.grid) this.grid.hidden = !this.state.expanded;
      const label = this.allBtn.querySelector('[data-rw-all-label]');
      const arrow = this.allBtn.querySelector('[data-rw-all-arrow]');
      if (label) label.textContent = this.state.expanded ? this.allBtn.dataset.close : this.allBtn.dataset.open;
      if (arrow) arrow.hidden = this.state.expanded;
      this.allBtn.setAttribute('aria-expanded', String(this.state.expanded));
      this.update();
    }
  }
  if (!customElements.get('tm-review-wall')) customElements.define('tm-review-wall', TMReviewWall);

  /* ---------- Bundle cards: set the form quantity, show the bundle price in the button ---------- */
  class TMBundles extends HTMLElement {
    connectedCallback() {
      this.qty = this.querySelector('[data-bundle-qty]');
      this.scope = this.closest('[data-product-scope]') || document;
      this.addEventListener('change', (e) => {
        if (e.target.matches('input[type="radio"]')) this.apply(e.target);
      });
      const checked = this.querySelector('input[type="radio"]:checked') || this.querySelector('input[type="radio"]');
      if (checked) {
        checked.checked = true;
        this.apply(checked, true);
      }
    }
    apply(radio, initial = false) {
      if (this.qty) this.qty.value = radio.value;
      const priceText = radio.dataset.priceText;
      if (priceText) {
        qsa('[data-main-atc] .dl-btn__price', this.scope).forEach((el) => (el.textContent = priceText));
      }
      if (!initial) document.dispatchEvent(new CustomEvent('tm:bundle:change', { detail: { quantity: parseInt(radio.value, 10) } }));
    }
  }
  if (!customElements.get('tm-bundles')) customElements.define('tm-bundles', TMBundles);

  /* ---------- Survey results: tabs + one-time ring / counter animation ---------- */
  class TMSurvey extends HTMLElement {
    connectedCallback() {
      this.tabs = qsa('[role="tab"]', this);
      this.panels = qsa('[role="tabpanel"]', this);
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          const n = this.tabs.length;
          let next = null;
          if (e.key === 'ArrowRight') next = (i + 1) % n;
          if (e.key === 'ArrowLeft') next = (i - 1 + n) % n;
          if (e.key === 'Home') next = 0;
          if (e.key === 'End') next = n - 1;
          if (next === null) return;
          e.preventDefault();
          this.select(next);
          this.tabs[next].focus();
        });
      });
      if (reduceMotion || !('IntersectionObserver' in window)) return;
      this.classList.add('is-armed');
      this.counters = qsa('[data-count]', this);
      this.counters.forEach((c) => (c.textContent = '0'));
      this.io = new IntersectionObserver((entries) => {
        if (!entries.some((en) => en.isIntersecting)) return;
        this.io.disconnect();
        this.play();
      }, { threshold: 0.25 });
      this.io.observe(this);
    }
    disconnectedCallback() {
      this.io && this.io.disconnect();
      this.raf && cancelAnimationFrame(this.raf);
    }
    select(index) {
      this.tabs.forEach((t, i) => {
        t.setAttribute('aria-selected', String(i === index));
        t.tabIndex = i === index ? 0 : -1;
      });
      this.panels.forEach((p, i) => (p.hidden = i !== index));
    }
    play() {
      requestAnimationFrame(() => this.classList.add('is-played'));
      const start = performance.now();
      const step = (now) => {
        const t = Math.min(1, (now - start) / 1050);
        const eased = 1 - Math.pow(1 - t, 3);
        this.counters.forEach((c) => (c.textContent = String(Math.round(eased * parseFloat(c.dataset.count)))));
        if (t < 1) this.raf = requestAnimationFrame(step);
      };
      this.raf = requestAnimationFrame(step);
    }
  }
  if (!customElements.get('tm-survey')) customElements.define('tm-survey', TMSurvey);

  /* ---------- Soft fade-in on scroll (once) ---------- */
  const fadeObserver =
    'IntersectionObserver' in window && !reduceMotion
      ? new IntersectionObserver(
          (entries) =>
            entries.forEach((en) => {
              if (!en.isIntersecting) return;
              en.target.classList.add('is-in');
              fadeObserver.unobserve(en.target);
            }),
          { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
        )
      : null;
  const initFade = (root = document) => {
    qsa('[data-tm-fade]:not(.is-in)', root).forEach((el) => (fadeObserver ? fadeObserver.observe(el) : el.classList.add('is-in')));
  };

  /* ---------- Size chart dialog ---------- */
  document.addEventListener('click', (e) => {
    const open = e.target.closest('[data-tm-size-open]');
    if (open) {
      const dlg = open.parentElement.querySelector('.tm-size__dialog');
      if (dlg && typeof dlg.showModal === 'function') dlg.showModal();
      return;
    }
    if (e.target.closest('[data-tm-size-close]') || e.target.matches('.tm-size__dialog')) {
      const dlg = e.target.closest('.tm-size__dialog');
      dlg && dlg.close();
    }
  });

  const boot = (root = document) => {
    initTickers(root);
    initReveal(root);
    initFade(root);
    window.TM && window.TM.sections && window.TM.sections.forEach((fn) => fn(root));
  };

  let resizeTimer;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => qsa('[data-tm-ticker]').forEach((t) => !reduceMotion && sizeTicker(t)), 200);
  });
  document.fonts && document.fonts.ready && document.fonts.ready.then(() => qsa('[data-tm-ticker]').forEach((t) => !reduceMotion && sizeTicker(t)));

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
